/** @jsxImportSource @emotion/react */
import React, {
  memo,
  useCallback,
  useState,
  useEffect,
  useMemo,
  useContext
} from 'react'
import take from 'lodash/take'
import debounce from 'lodash/debounce'
import {
  useDynamicPathReplacement,
  ACCOUNT_ID_REDIR_SUBST
} from '@signal/core/router/redirect'
import { UseDynamicPathReplacementHookType } from '@signal/core/router/redirect/types'
import {
  RecentlyViewedAccountType,
  UserAccountType
} from '@signal/core/user/types'
import { ActiveAccountContext } from '@signal/core/user/Context'
import withCommonSnapshotsOnly from '@signal/hoc/withCommonSnapshotsOnly'
import { useParams } from 'react-router-dom'
import {
  RecentAccountsSection,
  RecentAccountsList,
  RecentAccountItem,
  AccountName,
  DefaultMessage,
  LoadingMessage,
  AccountsScrollOverlayBottom,
  AllAccountsButton,
  accountItemVariants
} from './styles'
import { useScrollTracking, useMenuAccountSearch } from './utils'
import { MINIMUM_TERM_LENGTH } from '@signal/core/accountSearch/fetchHooks'
import PureInput from '@signal/components/PureInput'
import { OpenInNew, Search } from '@material-ui/icons'
import Link from '@signal/components/Link'

const MAX_RECENT_ACCOUNTS = 10
const MAX_SEARCH_RESULTS = 10
const SEARCH_DEBOUNCE_MS = 300

export interface AccountsProps {
  recentAccounts?: Array<RecentlyViewedAccountType>
  allAccounts?: Array<UserAccountType>
  onAccountChange: ({ accountId }: { accountId: string }) => void
  onCloseMenu: () => void
  useDynamicPathReplacementHook?: UseDynamicPathReplacementHookType
  isDarkMode?: boolean
}

const Accounts: React.FC<AccountsProps> = ({
  recentAccounts,
  allAccounts = [],
  onAccountChange,
  onCloseMenu,
  useDynamicPathReplacementHook = useDynamicPathReplacement,
  isDarkMode = false
}) => {
  const redirectURL = useDynamicPathReplacementHook()
  const { activeAccount } = useContext(ActiveAccountContext)
  const params = useParams<{ accountId?: string }>()

  // Search state
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')

  const trimmedSearchTerm = searchTerm.trim().toLowerCase()

  // Debounce the term we hand to the search endpoint
  const debounceSearchTerm = useMemo(
    () =>
      debounce((term: string) => {
        setDebouncedSearchTerm(term)
      }, SEARCH_DEBOUNCE_MS),
    []
  )

  useEffect(() => {
    debounceSearchTerm(trimmedSearchTerm)

    return () => {
      debounceSearchTerm.cancel()
    }
  }, [trimmedSearchTerm, debounceSearchTerm])

  const searchableAccountIds = useMemo(
    () => allAccounts.map(({ accountId }) => accountId),
    [allAccounts]
  )

  const { results: searchResults, isSearching: isFetchingResults } =
    useMenuAccountSearch(searchableAccountIds, debouncedSearchTerm)

  const filteredAccounts = useMemo(
    () => searchResults.slice(0, MAX_SEARCH_RESULTS),
    [searchResults]
  )

  const hasSearchableTerm = trimmedSearchTerm.length >= MINIMUM_TERM_LENGTH

  const isSearching =
    hasSearchableTerm &&
    (trimmedSearchTerm !== debouncedSearchTerm || isFetchingResults)

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value)
    },
    []
  )

  const handleClickRecentAccount = useCallback(
    (account: RecentlyViewedAccountType) => () => {
      onAccountChange({ accountId: account.accountId })
      onCloseMenu()
    },
    [onAccountChange, onCloseMenu]
  )

  const handleClickSearchResult = useCallback(
    (account: UserAccountType) => () => {
      onAccountChange({ accountId: account.accountId })
      onCloseMenu()
    },
    [onAccountChange, onCloseMenu]
  )

  const handleSearchResultAnchorClick = useCallback(
    (account: UserAccountType) => (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault()
      handleClickSearchResult(account)()
    },
    [handleClickSearchResult]
  )

  const handleRecentAccountAnchorClick = useCallback(
    (account: RecentlyViewedAccountType) =>
      (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault()
        handleClickRecentAccount(account)()
      },
    [handleClickRecentAccount]
  )

  // Helper function to create account-specific URLs
  const createAccountUrl = useCallback(
    (accountId: string) => {
      // Get the current account ID from URL params or active account context
      const currentAccountId = params.accountId || activeAccount?.accountId

      if (!currentAccountId) {
        // Fallback to the account selector URL if no current account
        return redirectURL.replace(ACCOUNT_ID_REDIR_SUBST, accountId)
      }

      const currentPath = window.location.pathname
      const currentSearch = window.location.search
      const currentHash = window.location.hash

      // Replace the current account ID in the path with the new one
      // Handle both dashed and dashless account ID formats
      const newPath = currentPath
        .replace(new RegExp(`\\b${currentAccountId}\\b`, 'g'), accountId)
        .replace(
          new RegExp(`\\b${currentAccountId.replace(/-/g, '')}\\b`, 'g'),
          accountId.replace(/-/g, '')
        )

      return `${newPath}${currentSearch}${currentHash}`
    },
    [params.accountId, activeAccount?.accountId, redirectURL]
  )

  // Determine what to display
  const showSearchResults = hasSearchableTerm
  const hasSearchResults = filteredAccounts.length > 0
  const showRecentAccounts =
    !showSearchResults && recentAccounts && recentAccounts.length > 0

  // Track scroll state for overlay visibility
  const { scrollRef, scrollState } = useScrollTracking([
    searchTerm,
    showSearchResults,
    showRecentAccounts,
    recentAccounts?.length,
    filteredAccounts.length
  ])

  return (
    <RecentAccountsSection $isDarkMode={isDarkMode}>
      <PureInput
        $stretch
        value={searchTerm}
        onChange={handleSearchChange}
        placeholder="Search Accounts..."
        leftOrnament={<Search fontSize="small" />}
      />
      <RecentAccountsList $isDarkMode={isDarkMode} ref={scrollRef}>
        {isSearching ? (
          <LoadingMessage $isDarkMode={isDarkMode}>Searching...</LoadingMessage>
        ) : showSearchResults ? (
          hasSearchResults ? (
            filteredAccounts.map((account, index) => (
              <a
                key={account.accountId}
                href={createAccountUrl(account.accountId)}
                style={{ textDecoration: 'none', color: 'inherit' }}
                onClick={handleSearchResultAnchorClick(account)}
              >
                <RecentAccountItem
                  variants={accountItemVariants}
                  initial="hidden"
                  animate="visible"
                  transition={{ delay: index * 0.05 }}
                  $isDarkMode={isDarkMode}
                >
                  <AccountName>{account.accountName}</AccountName>
                </RecentAccountItem>
              </a>
            ))
          ) : (
            <DefaultMessage $isDarkMode={isDarkMode}>
              No accounts found for "{searchTerm}"
            </DefaultMessage>
          )
        ) : showRecentAccounts ? (
          take(recentAccounts, MAX_RECENT_ACCOUNTS).map((account, index) => (
            <a
              key={account.accountId}
              href={createAccountUrl(account.accountId)}
              style={{ textDecoration: 'none', color: 'inherit' }}
              onClick={handleRecentAccountAnchorClick(account)}
            >
              <RecentAccountItem
                variants={accountItemVariants}
                initial="hidden"
                animate="visible"
                transition={{ delay: index * 0.05 }}
                $isDarkMode={isDarkMode}
              >
                <AccountName>{account.accountName}</AccountName>
              </RecentAccountItem>
            </a>
          ))
        ) : (
          <DefaultMessage $isDarkMode={isDarkMode}>
            No Recent Accounts
          </DefaultMessage>
        )}
      </RecentAccountsList>

      {/* Scroll overlay at bottom - only show when can scroll and not at bottom */}
      {scrollState.canScroll && !scrollState.isAtBottom && (
        <AccountsScrollOverlayBottom $isDarkMode={isDarkMode} />
      )}
      <Link
        to={redirectURL}
        style={{ textDecoration: 'none' }}
        onClick={onCloseMenu}
      >
        <AllAccountsButton>
          All Accounts <OpenInNew />
        </AllAccountsButton>
      </Link>
    </RecentAccountsSection>
  )
}

export default memo(withCommonSnapshotsOnly(Accounts))
